import _ from 'lodash';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useQuery, useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import React, { useReducer, useEffect, useState } from 'react';
import { useDebounce } from '../../../../core/hooks';

import { Container, Loader } from './styles';

import ContainerVideo from './container-video';
import ListClips from './list-clips';
import MaximumTimeModal from './maximum-time-modal';
import Tabs from '../../../constants/tabs';
import withLayout from '../../../../core/components/with-layout';

import reducer, {
  initialState,
  isClipsEqual,
} from './reducer';
import useDispatchToActions from '../../../../core/utils/dispatch';
import ClipModal from './list-clips/clip-modal';

import {
  addClip,
  closeMaxTimeModal,
  moveClip,
  removeClip,
  setSaving,
  setFeedbackSaved,
  setTitle,
  setHighlightReel,
  closeClipModal,
  openClipModal,
} from './actions';

import {
  CREATE_HIGHLIGHT_REEL,
  GET_HIGHLIGHT_REEL_BY_ID,
  UPDATE_HIGHLIGHT_REEL,
} from '../graphql';

const DEBOUNCE_TIME = 700;
const POLL_INTERVAL = 3000;

const CreateHighlightReel = (props) => {
  const {
    companyId: currentCompanyId,
    history,
    match,
  } = props;

  const { params } = match;
  const { highlightReelId } = params;

  const [state, dispatch] = useReducer(reducer, initialState);
  const dispatchToActions = useDispatchToActions({
    addClip,
    closeMaxTimeModal,
    moveClip,
    removeClip,
    setFeedbackSaved,
    setHighlightReel,
    setSaving,
    setTitle,
    closeClipModal,
    openClipModal,
  }, dispatch);

  const {
    addClip: onAddClip,
    moveClip: onMoveClip,
    removeClip: onRemoveClip,
    closeMaxTimeModal: onCloseMaxTimeModal,
    setFeedbackSaved: onSetFeedbackSaved,
    setHighlightReel: onSetHighlightReel,
    setSaving: onSetSaving,
    setTitle: onSetTitle,
    closeClipModal: onCloseClipModal,
    openClipModal: onOpenClipModal,
  } = dispatchToActions;

  const {
    data = {},
    loading,
  } = useQuery(GET_HIGHLIGHT_REEL_BY_ID, {
    variables: { id: highlightReelId },
    skip: !highlightReelId,
    pollInterval: POLL_INTERVAL,
  });
  const [createHighlightReel] = useMutation(CREATE_HIGHLIGHT_REEL);
  const [updateHighlightReel] = useMutation(UPDATE_HIGHLIGHT_REEL);

  const { highlightReel = {} } = data;

  const {
    clips,
    clipsMap,
    feedback,
    maxTimeModalOpened,
    touched,
    title,
    status,
    totalClips,
    videoUrl,
    showClipModal,
    selectedNoteId,
  } = state;

  const companyId = highlightReel.companyId || currentCompanyId;

  const [inserting, setInserting] = useState(false);

  useEffect(() => {
    if (!loading && !_.isEmpty(highlightReel)) {
      onSetHighlightReel(highlightReel);
    }
  }, [loading, highlightReel, onSetHighlightReel]);

  const handleRedirect = (id) => {
    history.push(`/highlight-reel/${id}/edit`);
  };

  const handleCreate = async (attributes) => {
    const { data: newData } = await createHighlightReel({
      variables: {
        attributes,
      },
    });

    const { id } = newData.createHighlightReel.highlightReel;
    if (!id) setInserting(false);
    handleRedirect(id);
  };

  const handleUpdate = (attributes) => (
    updateHighlightReel({
      variables: {
        id: highlightReel.id,
        attributes,
      },
    })
  );

  useDebounce(async () => {
    if (loading || !touched) return;

    let anyChanges = false;

    let attributes = {
      companyId,
      title,
    };

    // Only save title if it had any change compared to server
    if (title !== highlightReel.title) {
      attributes = {
        ...attributes,
        title,
      };

      anyChanges = true;
    }

    // Only save clips if there is any change compared to server
    if (!isClipsEqual(clips, highlightReel.highlightReelClips)) {
      const highlightReelClips = _.map(clips, (clip) => ({
        highlightReelId: clip.highlightReelId,
        testSessionNoteId: clip.testSessionNote.id,
        clipOrder: clip.clipOrder,
      }));

      attributes = {
        ...attributes,
        highlightReelClips,
      };

      anyChanges = true;
    }

    // If there were no changes compared to server, don't save.
    if (!anyChanges) return;

    onSetSaving();

    if (highlightReel && highlightReel.id) {
      await handleUpdate(attributes);
    } else {
      if (inserting) return; // prevent multiple insert and skip onSetFeedbackSaved
      setInserting(true);
      await handleCreate(attributes);
    }

    onSetFeedbackSaved();
  }, DEBOUNCE_TIME, [clips, title]);

  const handleTitleChange = ({ target }) => {
    const { value: newTitle } = target;
    onSetTitle(newTitle);
  };

  const handleAddClip = (testSessionNote, index) => {
    onAddClip(
      testSessionNote,
      highlightReelId,
      index,
      clips,
    );
  };

  if (loading && !touched) {
    return (
      <Container>
        <Loader data-loader="create-highlight-loader" />
      </Container>
    );
  }

  const handleCloseClip = () => {
    onCloseClipModal();
  };

  const handleShowClip = (id) => {
    onOpenClipModal(id);
  };

  return (
    <Container>
      {maxTimeModalOpened && (
        <MaximumTimeModal
          onClose={onCloseMaxTimeModal}
        />
      )}
      <ClipModal
        open={showClipModal}
        onClose={handleCloseClip}
        noteId={selectedNoteId}
        onAddClip={(testSessionNote) => handleAddClip(testSessionNote, clips.length)}
        clipsMap={clipsMap}
      />
      <DndProvider backend={HTML5Backend}>
        <ListClips
          companyId={companyId}
          clipsMap={clipsMap}
          onShowClip={handleShowClip}
        />
        <ContainerVideo
          clips={clips}
          companyId={companyId}
          feedback={feedback}
          status={status}
          totalClips={totalClips}
          videoUrl={videoUrl}
          title={title}
          onTitleChange={handleTitleChange}
          onMoveClip={onMoveClip}
          onRemoveClip={onRemoveClip}
          onAddClip={(testSessionNote, index) => handleAddClip(testSessionNote, index)}
          onShowClip={handleShowClip}
        />
      </DndProvider>
    </Container>
  );
};

CreateHighlightReel.propTypes = {
  companyId: PropTypes.string.isRequired,
  highlightReelId: PropTypes.string,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.object,
  }).isRequired,
};

CreateHighlightReel.defaultProps = {
  highlightReelId: '',
};

const mapStateToProps = ({ authentication }) => ({
  companyId: authentication.company?.id,
});

const enhance = compose(
  connect(mapStateToProps, null),
  withLayout({ selectedTab: Tabs.HIGHLIGHT_REEL }),
);

export default enhance(CreateHighlightReel);
